import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { usePVData } from '../context/PVDataContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSun, FiZap, FiTrendingUp, FiActivity, FiCalendar, FiTree, FiCloud, FiDroplet, FiWind } = FiIcons;

const Dashboard = ({ setCurrentPage }) => {
  const { plants, getMonthlyData, getYearlyData } = usePVData();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Berechne Gesamtstatistiken
  const totalCapacity = plants.reduce((sum, plant) => sum + parseFloat(plant.capacity || 0), 0);
  const totalProduction = plants.reduce((sum, plant) => sum + getYearlyData(plant.id, selectedYear), 0);
  const previousYearProduction = plants.reduce((sum, plant) => sum + getYearlyData(plant.id, selectedYear - 1), 0);

  // Monatliche Daten für Diagramm mit Vorjahresvergleich
  const monthlyChartData = Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    const monthName = new Date(2024, i).toLocaleDateString('de-DE', { month: 'short' });
    
    const currentYearTotal = plants.reduce((sum, plant) => {
      return sum + getMonthlyData(plant.id, month, selectedYear);
    }, 0);
    
    const previousYearTotal = plants.reduce((sum, plant) => {
      return sum + getMonthlyData(plant.id, month, selectedYear - 1);
    }, 0);

    return {
      month: monthName,
      currentYear: currentYearTotal,
      previousYear: previousYearTotal,
      monthNum: month
    };
  });

  // Anlagen-Performance Daten mit Vorjahresvergleich
  const plantPerformanceData = plants.map(plant => ({
    name: plant.name,
    currentYear: getYearlyData(plant.id, selectedYear),
    previousYear: getYearlyData(plant.id, selectedYear - 1),
    capacity: parseFloat(plant.capacity || 0)
  }));

  const COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#f97316'];

  // CO2-Einsparung Faktoren (Durchschnittswerte)
  const CO2_PER_KWH = 0.401; // kg CO2 pro kWh (deutscher Strommix)
  const TREES_PER_TON_CO2 = 45; // Anzahl Bäume die ca. 1 Tonne CO2 pro Jahr binden
  const WATER_PER_KWH = 1.5; // Liter Wasser gespart pro kWh Solarstrom
  const KM_PER_KWH = 6.2; // Kilometer eines durchschnittlichen Elektroautos pro kWh

  // Berechne alle bisherigen Ertragsdaten
  const allYearsProduction = years.reduce((sum, year) => {
    const yearlyProduction = plants.reduce((yearSum, plant) => yearSum + getYearlyData(plant.id, year), 0);
    return sum + yearlyProduction;
  }, 0);

  // Umweltauswirkungen für alle Jahre
  const totalCo2Saved = (allYearsProduction * CO2_PER_KWH) / 1000; // Tonnen CO2
  const totalTreeEquivalent = Math.round(totalCo2Saved * TREES_PER_TON_CO2);
  const totalWaterSaved = Math.round(allYearsProduction * WATER_PER_KWH); // Liter Wasser
  const totalElectricCarKm = Math.round(allYearsProduction * KM_PER_KWH); // Kilometer mit E-Auto

  // Dashboard-Statistiken mit Umweltauswirkungen
  const environmentalStats = [
    {
      title: "CO₂-Einsparung Gesamt",
      value: `${totalCo2Saved.toFixed(1)} t`,
      icon: FiCloud,
      color: "bg-blue-100 text-blue-600",
      description: "Seit Inbetriebnahme"
    },
    {
      title: "Bäume gepflanzt",
      value: totalTreeEquivalent.toLocaleString(),
      icon: FiTree,
      color: "bg-green-100 text-green-600",
      description: "Äquivalent"
    },
    {
      title: "Wasser gespart",
      value: `${(totalWaterSaved / 1000).toFixed(1)} m³`,
      icon: FiDroplet,
      color: "bg-cyan-100 text-cyan-600",
      description: "Verglichen mit konv. Energie"
    },
    {
      title: "E-Auto Reichweite",
      value: `${(totalElectricCarKm / 1000).toFixed(1)} Tkm`,
      icon: FiZap,
      color: "bg-purple-100 text-purple-600",
      description: "Mit erzeugtem Strom"
    },
  ];

  // Berechne Veränderung zum Vorjahr
  const yearOverYearChange = previousYearProduction > 0 
    ? ((totalProduction - previousYearProduction) / previousYearProduction * 100).toFixed(1)
    : 0;
  const isPositiveChange = yearOverYearChange >= 0;

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Übersicht Ihrer PV-Anlagen</p>
        </div>
        <div className="flex items-center space-x-4">
          <SafeIcon icon={FiCalendar} className="text-gray-500" />
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </motion.div>

      {/* Statistik Karten */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Anlagen gesamt</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{plants.length}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <SafeIcon icon={FiSun} className="text-blue-600 text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Gesamtkapazität</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{totalCapacity.toFixed(1)} kWp</p>
            </div>
            <div className="bg-solar-100 p-3 rounded-lg">
              <SafeIcon icon={FiZap} className="text-solar-600 text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Jahresertrag {selectedYear}</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{totalProduction.toLocaleString()} kWh</p>
              {previousYearProduction > 0 && (
                <div className="flex items-center mt-1">
                  <span className={`text-xs font-medium ${isPositiveChange ? 'text-green-600' : 'text-red-600'}`}>
                    {isPositiveChange ? '+' : ''}{yearOverYearChange}%
                  </span>
                  <span className="text-xs text-gray-500 ml-1">vs. Vorjahr</span>
                </div>
              )}
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <SafeIcon icon={FiTrendingUp} className="text-green-600 text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Jahresertrag {selectedYear - 1}</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{previousYearProduction.toLocaleString()} kWh</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <SafeIcon icon={FiActivity} className="text-purple-600 text-xl" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Diagramme */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Monatlicher Ertrag - Vergleich {selectedYear} vs. {selectedYear - 1}
          </h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [`${value.toLocaleString()} kWh`, '']}
                  labelFormatter={(label) => `Monat: ${label}`}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="currentYear"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }}
                  name={`${selectedYear}`}
                />
                <Line
                  type="monotone"
                  dataKey="previousYear"
                  stroke="#9ca3af"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#9ca3af', strokeWidth: 2, r: 4 }}
                  name={`${selectedYear - 1}`}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Anlagen-Performance - Vergleich {selectedYear} vs. {selectedYear - 1}
          </h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={plantPerformanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [`${value.toLocaleString()} kWh`, '']}
                />
                <Legend />
                <Bar 
                  dataKey="currentYear" 
                  fill="#f59e0b" 
                  name={`${selectedYear}`}
                />
                <Bar 
                  dataKey="previousYear" 
                  fill="#d1d5db" 
                  name={`${selectedYear - 1}`}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Anlagenverteilung */}
      {plants.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Ertragsverteilung nach Anlagen - Vergleich {selectedYear} vs. {selectedYear - 1}
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Pie Chart für aktuelles Jahr */}
            <div>
              <h4 className="text-center font-medium text-gray-700 mb-2">{selectedYear}</h4>
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={plantPerformanceData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="currentYear"
                    >
                      {plantPerformanceData.map((entry, index) => (
                        <Cell key={`cell-current-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value.toLocaleString()} kWh`, 'Ertrag']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Pie Chart für Vorjahr */}
            <div>
              <h4 className="text-center font-medium text-gray-700 mb-2">{selectedYear - 1}</h4>
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={plantPerformanceData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="previousYear"
                    >
                      {plantPerformanceData.map((entry, index) => (
                        <Cell key={`cell-previous-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value.toLocaleString()} kWh`, 'Ertrag']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Detaillierte Vergleichstabelle */}
            <div className="space-y-3 overflow-y-auto max-h-[300px]">
              <h4 className="font-medium text-gray-900">Anlagen-Details Vergleich</h4>
              {plantPerformanceData.map((plant, index) => {
                const change = plant.previousYear > 0 
                  ? ((plant.currentYear - plant.previousYear) / plant.previousYear * 100).toFixed(1)
                  : 0;
                const isPositive = change >= 0;

                return (
                  <div key={plant.name} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3 mb-2">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm md:text-base">{plant.name}</p>
                        <p className="text-xs md:text-sm text-gray-600">{plant.capacity} kWp</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-gray-600">{selectedYear}:</p>
                        <p className="font-medium text-gray-900">{plant.currentYear.toLocaleString()} kWh</p>
                      </div>
                      <div>
                        <p className="text-gray-600">{selectedYear - 1}:</p>
                        <p className="font-medium text-gray-900">{plant.previousYear.toLocaleString()} kWh</p>
                      </div>
                    </div>
                    {plant.previousYear > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                          {isPositive ? '+' : ''}{change}% vs. Vorjahr
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}

      {/* Umwelt-Dashboard */}
      {plants.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
        >
          <div className="flex items-start justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <SafeIcon icon={FiTree} className="text-green-600 mr-2" />
                Umweltauswirkungen
              </h3>
              <p className="text-gray-600 mt-1">Positiver Umweltbeitrag Ihrer Anlagen</p>
            </div>
            <button
              onClick={() => setCurrentPage('environmental')}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
            >
              Details ansehen
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {environmentalStats.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 + index * 0.1 }}
                className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm text-gray-600">{stat.title}</h4>
                    <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  <div className={`rounded-full p-3 ${stat.color}`}>
                    <SafeIcon icon={stat.icon} className="text-lg" />
                  </div>
                </div>
                <p className="text-xs text-gray-500">{stat.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
              className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100"
            >
              <div className="flex items-start space-x-4">
                <div className="rounded-full p-3 bg-green-100">
                  <SafeIcon icon={FiWind} className="text-green-600 text-xl" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Vermiedene Emissionen</h4>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <p className="text-sm text-gray-600">Schwefeldioxid</p>
                      <p className="text-lg font-bold text-gray-900">{(totalCo2Saved * 1.5).toFixed(1)} kg</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Stickoxide</p>
                      <p className="text-lg font-bold text-gray-900">{(totalCo2Saved * 0.8).toFixed(1)} kg</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100"
            >
              <div className="flex items-start space-x-4">
                <div className="rounded-full p-3 bg-blue-100">
                  <SafeIcon icon={FiZap} className="text-blue-600 text-xl" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Energieeinsparung</h4>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <p className="text-sm text-gray-600">Kohle gespart</p>
                      <p className="text-lg font-bold text-gray-900">{(allYearsProduction * 0.33 / 1000).toFixed(1)} t</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Erdgas gespart</p>
                      <p className="text-lg font-bold text-gray-900">{(allYearsProduction * 0.2).toFixed(0)} m³</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}

      {plants.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm p-6 md:p-12 border border-gray-100 text-center"
        >
          <SafeIcon icon={FiSun} className="text-gray-300 text-4xl md:text-6xl mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Keine Anlagen vorhanden</h3>
          <p className="text-gray-600 mb-6">Fügen Sie Ihre erste PV-Anlage hinzu, um das Dashboard zu nutzen.</p>
          <button
            onClick={() => setCurrentPage('plants')}
            className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
          >
            Erste Anlage hinzufügen
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default Dashboard;